<template>
  <div class="gpi-form">
    <TextField
      v-model="name"
      name="name"
      label="Cardholders Name"
      class="full-width"
      :validation-errors="v$.$errors"
    />
    <div class="d-flex align-items-center mb-2">
      <div class="w-100">
        <div class="label">Card Number</div>
        <div id="card-number" class="gpi-field"></div>
      </div>
      <div class="mx-2" style="width: 300px">
        <div class="label">Exp</div>
        <div id="card-expiration" class="gpi-field"></div>
      </div>
      <div style="width: 200px">
        <div class="label">CVV</div>
        <div id="card-cvv" class="gpi-field"></div>
      </div>
    </div>
    <BillingAddressForm ref="billingAddressForm" :address="billingAddress" />
    <div id="submit" class="submit-container"></div>
    <div
      v-if="config && config.card_savable && !autoSaveCard"
      class="d-flex flex-column mb-2"
    >
      <label class="mb-0 ml-2" style="font-size: 1.1em">
        <input v-model="saveCard" type="checkbox" />
        Save for future payments.
      </label>
      <span class="ml-2" style="font-size: 0.9em"
        >(Saving this payment method replaces any previously saved payment
        method on file.)</span
      >
    </div>
  </div>
</template>

<script>
/* eslint-disable simple-import-sort/imports */
import { nextTick } from "vue";

import TextField from "@/components/TextField.vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import BillingAddressForm from "./BillingAddressForm.vue";
import usePaymentMethods from "../usePaymentMethods";
import { loadGlobalPayments } from "../paymentSdks";
export default {
  components: {
    TextField,
    BillingAddressForm
  },
  props: {
    autoSaveCard: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  setup() {
    const { billingAddress, paymentMethodsResponse } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      billingAddress,
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      name: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      form: null,
      name: "",
      resolver: null,
      rejector: null,
      saveCard: false
    };
  },
  computed: {
    config() {
      return this.paymentMethodsResponse.find(
        (method) => method.key === "gpi_card"
      );
    }
  },
  async mounted() {
    try {
      await loadGlobalPayments();
      if (this.sdkLoadAborted) return;
      await nextTick();
      if (this.sdkLoadAborted) return;
      this.initForm();
      this.attachSuccessHandler();
      this.attachErrorHandler();
    } catch (error) {
      window.notify.error(
        "Unable to load the payment form. Please refresh and try again."
      );
      window.Sentry?.captureException(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  },
  beforeUnmount() {
    // The SDK load is async; if the form unmounts first, skip initForm so the
    // SDK can't bind its hosted-field iframe onto another form's shared DOM ids.
    this.sdkLoadAborted = true;
  },
  methods: {
    attachErrorHandler() {
      this.form.on("token-error", (resp) => {
        // show error to the consumer
        window.notify.error(resp.error.message);
        this.rejector(resp.error.message);
      });
    },
    attachSuccessHandler() {
      this.form.on("token-success", async (resp) => {
        this.resolver({
          ...(await this.$refs.billingAddressForm.getData()),
          name: this.name,
          card_token: resp.temporary_token,
          card_save_payment_method: this.saveCard || this.autoSaveCard
        });
      });
    },
    async getData() {
      await this.$refs.billingAddressForm.getData();
      const validated = await this.v$.$validate();
      return await new Promise((resolve, reject) => {
        this.resolver = resolve;
        this.rejector = reject;
        if (validated) {
          try {
            // Click submit button
            this.form.frames.submit.events.click[0]();
          } catch (e) {
            // Capture payment form submit errors
            throw new Error(
              "Unable to submit GPI Payment form with error: " + e
            );
          }
        } else {
          reject();
        }
      });
    },
    initForm() {
      if (!this.config) {
        return;
      }
      GlobalPayments.configure({
        "X-GP-Api-Key": this.config.tokenization_key,
        "X-GP-Environment": this.config.environment
      });
      this.form = GlobalPayments.ui.form({
        fields: {
          "card-number": {
            target: "#card-number", //renders field to <div id="card-number">
            placeholder: "•••• •••• •••• ••••" //Optional
          },
          "card-expiration": {
            target: "#card-expiration",
            placeholder: "MM / YYYY"
          },
          "card-cvv": {
            target: "#card-cvv",
            placeholder: "•••"
          },
          submit: {
            text: "Validate Card", //Set text for Submit button
            target: "#submit"
          }
        },
        styles: {
          "button, input": {
            outline: "none",
            "border-width": "0",
            "letter-spacing": "2px"
          },
          "input, select": {
            display: "block",
            width: "100%",
            padding: ".375rem .75rem",
            "line-height": "1.5",
            color: "#495057",
            "background-color": "#fff !important",
            "background-clip": "padding-box",
            border: "1px solid #ced4da",
            "border-radius": ".25rem",
            "font-size": "14px"
          },
          "input:focus": {
            "-webkit-box-shadow": "inset 0px 0px 3px 0px #b0dcff !important",
            "box-shadow": "inset 0px 0px 3px 0px #b0dcff !important",
            "border-color": "#b0dcff !important"
          },
          button: {
            background: "#125d85",
            padding: "7px 40px",
            color: "white",
            "font-size": "15px",
            display: "none"
          },
          "button:active": {
            filter: "brightness(112%)"
          },
          "input.invalid": {
            "border-color": "#dc3545",
            "background-color": "#f7e5e5"
          },
          ".card-number.card-type-visa": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-visa@2x.png) no-repeat right",
            "background-position-y": "1px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-mastercard": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-mastercard@2x.png) no-repeat right",
            "background-position-y": "1px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-discover": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-discover@2x.png) no-repeat right",
            "background-position-y": "2px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-jcb": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-jcb@2x.png) no-repeat right",
            "background-position-y": "2px",
            "background-size": "60px 64px"
          },
          ".card-number.card-type-amex": {
            background:
              "rgba(0,0,0,0) url(https://hps.github.io/token/gp-1.0.0/images/logo-amex@2x.png) no-repeat right",
            "background-position-y": "4px",
            "background-size": "60px 64px"
          }
        }
      });

      if (this.customer) {
        this.name =
          this.customer.full_name ||
          `${this.customer.first_name} ${this.customer.last_name}`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.gpi-form {
  :deep(iframe) {
    width: 100%;
    min-height: 39px;
  }
  .label {
    margin-bottom: 3px;
  }
  .submit-container {
    display: none;
  }
  .gpi-field {
    height: 35px;
    background-color: #fff;
    border-radius: 4px;
  }
}
</style>
